"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NavBar = NavBar;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("@iconify/react");
var react_2 = require("react");
var react_router_dom_1 = require("react-router-dom");
var usehooks_ts_1 = require("usehooks-ts");
var common_1 = require("../features/slices/common");
var hooks_1 = require("../store/hooks");
var constants_1 = require("./constants");
var navSections = [
    {
        title: "General",
        items: [
            {
                key: "dashboard",
                label: "Dashboard",
                icon: "solar:widget-2-linear",
                path: "/dashboard/home",
            },
            {
                key: "assets",
                label: "Assets",
                icon: "solar:buildings-2-linear",
                path: "/dashboard/assets",
            },
            {
                key: "areas",
                label: "Areas",
                icon: "solar:home-2-linear",
                path: "/dashboard/areas",
            },
            {
                key: "meters",
                label: "Meters",
                icon: "solar:bolt-linear",
                path: "/dashboard/meters",
            },
            {
                key: "tenants",
                label: "Tenants",
                icon: "solar:users-group-rounded-linear",
                path: "/dashboard/tenants",
            },
            {
                key: "services",
                label: "Services",
                icon: "solar:settings-minimalistic-linear",
                path: "/dashboard/services",
            },
        ],
    },
    {
        title: "Utilities",
        items: [
            {
                key: "meter-readings",
                label: "Meter Readings",
                icon: "solar:chart-square-linear",
                path: "/dashboard/meter-readings",
            },
            {
                key: "analysis",
                label: "Billing",
                icon: "solar:chart-2-linear",
                path: "/dashboard/analysis",
            },
            {
                key: "alterations",
                label: "Alterations",
                icon: "solar:bill-cross-linear",
                path: "/dashboard/alterations",
            },
            {
                key: "usage",
                label: "Usage",
                icon: "solar:bolt-linear",
                path: "/dashboard/usage",
            },
            {
                key: "rate-periods",
                label: "Rate Periods",
                icon: "solar:calendar-linear",
                path: "/dashboard/rate-periods",
            },
            // RF-2: the cost side — what the landlord pays suppliers, against what Cue recovers.
            {
                key: "supplier-bills",
                label: "Supplier Bills",
                icon: "solar:bill-list-linear",
                path: "/dashboard/supplier-bills",
            },
        ],
    },
    {
        title: "Settings",
        items: [
            // MC&LCC-2: reachable on its own, not only from the prompt on the area/asset/meter/billing screens —
            // someone coming back to finish the work shouldn't have to wait for a banner to find it. Not
            // adminOnly: linking premises is configuration work, unlike the integration connection itself.
            {
                key: "onboarding-gaps",
                label: "Onboarding Gaps",
                icon: "solar:link-circle-linear",
                path: "/dashboard/onboarding-gaps",
            },
            {
                key: "integrations",
                label: "Integrations",
                icon: "solar:plug-circle-linear",
                path: "/dashboard/integrations",
                adminOnly: true,
            },
            {
                key: "users",
                label: "Users",
                icon: "solar:users-group-rounded-linear",
                path: "/dashboard/users",
            },
            {
                key: "removal-requests",
                label: "Removal Requests",
                icon: "solar:flag-linear",
                path: "/dashboard/removal-requests",
            },
        ],
    },
];
function NavBar() {
    var navigate = (0, react_router_dom_1.useNavigate)();
    var location = (0, react_router_dom_1.useLocation)();
    var dispatch = (0, hooks_1.useAppDispatch)();
    var matchesTablet = (0, usehooks_ts_1.useMediaQuery)(constants_1.MEDIA_QUERIES.tablet);
    var isNavbarCollapsed = (0, hooks_1.useAppSelector)(function (state) { return state.common; }).isNavbarCollapsed;
    var role = (0, hooks_1.useAppSelector)(function (state) { return state.auth; }).role;
    var isAdmin = role === "Admin"; // XInt-2: "Admin" is Cue's SysAdmin
    // Hide SysAdmin-only items (e.g. Integrations) for non-admins; drop any section left empty as a result.
    var visibleSections = navSections
        .map(function (section) { return (__assign(__assign({}, section), { items: section.items.filter(function (item) { return !item.adminOnly || isAdmin; }) })); })
        .filter(function (section) { return section.items.length > 0; });
    (0, react_2.useEffect)(function () {
        if (matchesTablet) {
            dispatch((0, common_1.setNavBarCollapsed)(true));
        }
    }, [matchesTablet]);
    var isActive = function (path) { return location.pathname === path; };
    var handleToggle = function () {
        dispatch((0, common_1.setNavBarCollapsed)(!isNavbarCollapsed));
    };
    var handleNavigate = function (path) {
        navigate(path);
        if (matchesTablet) {
            dispatch((0, common_1.setNavBarCollapsed)(true));
        }
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [!isNavbarCollapsed && ((0, jsx_runtime_1.jsx)("div", { className: "fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden", onClick: handleToggle })), (0, jsx_runtime_1.jsxs)("div", { className: "fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden bg-slate-900 transition-all duration-300 ".concat(isNavbarCollapsed ? "w-16 md:w-20" : "w-64"), children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-3 border-b border-slate-700/50 px-4 py-5", children: [(0, jsx_runtime_1.jsx)("button", { onClick: handleToggle, className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-500 text-white transition-colors hover:bg-teal-400", children: (0, jsx_runtime_1.jsx)(react_1.Icon, { icon: isNavbarCollapsed ? "solar:hamburger-menu-linear" : "solar:close-circle-linear", className: "text-lg" }) }), !isNavbarCollapsed && (0, jsx_runtime_1.jsx)("h1", { className: "text-xl font-semibold tracking-tight text-white", children: "Let'sCue" })] }), (0, jsx_runtime_1.jsx)("nav", { className: "flex-1 space-y-1 overflow-y-auto px-3 py-4", children: visibleSections.map(function (section) { return ((0, jsx_runtime_1.jsxs)("div", { className: "mb-4", children: [!isNavbarCollapsed && (0, jsx_runtime_1.jsx)("p", { className: "mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400", children: section.title }), (0, jsx_runtime_1.jsx)("div", { className: "space-y-0.5", children: section.items.map(function (item) { return ((0, jsx_runtime_1.jsxs)("button", { onClick: function () { return handleNavigate(item.path); }, className: "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ".concat(isActive(item.path) ? "bg-teal-500/15 text-teal-400" : "text-slate-300 hover:bg-slate-800 hover:text-white", " ").concat(isNavbarCollapsed ? "justify-center" : ""), children: [(0, jsx_runtime_1.jsx)(react_1.Icon, { icon: isActive(item.path) ? item.icon.replace("-linear", "-bold") : item.icon, className: "shrink-0 text-lg" }), !isNavbarCollapsed && (0, jsx_runtime_1.jsx)("span", { children: item.label })] }, item.key)); }) })] }, section.title)); }) }), (0, jsx_runtime_1.jsx)("div", { className: "border-t border-slate-700/50 px-3 py-4", children: !isNavbarCollapsed ? ((0, jsx_runtime_1.jsxs)("div", { className: "rounded-lg bg-slate-800 px-3 py-3", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2.5", children: [(0, jsx_runtime_1.jsx)("div", { className: "flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/20", children: (0, jsx_runtime_1.jsx)(react_1.Icon, { icon: "solar:bolt-circle-bold", className: "text-lg text-teal-400" }) }), (0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)("p", { className: "text-sm font-semibold text-white", children: "Let'sCue" }), (0, jsx_runtime_1.jsx)("p", { className: "text-[11px] text-slate-400", children: "Utility Billing System" })] })] }), (0, jsx_runtime_1.jsx)("p", { className: "mt-2 text-center text-[10px] text-slate-500", children: "v1.0.0" })] })) : ((0, jsx_runtime_1.jsx)("div", { className: "flex justify-center", children: (0, jsx_runtime_1.jsx)("div", { className: "flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/20", children: (0, jsx_runtime_1.jsx)(react_1.Icon, { icon: "solar:bolt-circle-bold", className: "text-lg text-teal-400" }) }) })) })] })] }));
}
